import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  Select,
  message,
  Tabs,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Popconfirm,
  Tooltip,
  DatePicker,
  Drawer,
  Dropdown,
  List,
  Avatar,
  Spin,
  Timeline,
  Descriptions,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  CommentOutlined,
  SendOutlined,
  PaperClipOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  MoreOutlined,
  MailOutlined,
  CheckCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface ServiceType {
  value: string;
  label: string;
}

interface ServiceTypeDashboardStats {
  service_type: string;
  service_type_label: string;
  in_progress: number;
  completed: number;
}

interface DashboardStats {
  month?: string;
  start_date?: string;
  end_date?: string;
  by_service_type: ServiceTypeDashboardStats[];
}

interface CompletionEmailPreview {
  subject: string;
  to: string;
  business_name: string;
}

interface ActivityEvent {
  type: string;
  label: string;
  occurred_at?: string;
  detail?: {
    actor?: string | null;
    change?: string | null;
    action?: string;
    entity_type?: string;
  };
}

type DashboardTimeframe = 'this_month' | 'last_month' | 'last_7_days' | 'last_30_days' | 'custom';

const DASHBOARD_TIMEFRAME_OPTIONS: { value: DashboardTimeframe; label: string }[] = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom' },
];

const getDashboardDateRange = (
  timeframe: DashboardTimeframe,
  customRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
) => {
  const now = dayjs();
  switch (timeframe) {
    case 'last_month': {
      const lastMonth = now.subtract(1, 'month');
      return { start: lastMonth.startOf('month'), end: lastMonth.endOf('month') };
    }
    case 'last_7_days':
      return { start: now.subtract(6, 'day').startOf('day'), end: now.endOf('day') };
    case 'last_30_days':
      return { start: now.subtract(29, 'day').startOf('day'), end: now.endOf('day') };
    case 'custom':
      if (customRange?.[0] && customRange?.[1]) {
        return { start: customRange[0].startOf('day'), end: customRange[1].endOf('day') };
      }
      return { start: now.startOf('month'), end: now.endOf('month') };
    case 'this_month':
    default:
      return { start: now.startOf('month'), end: now.endOf('month') };
  }
};

const formatDashboardPeriod = (start?: string, end?: string) => {
  if (!start || !end) return '';
  return `${dayjs(start).format('MMM D, YYYY')} – ${dayjs(end).format('MMM D, YYYY')}`;
};

interface Business {
  id: string;
  business_name: string;
  mid?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

interface Agent {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
}

interface CreditCardServiceTask {
  id: string;
  business_id: string;
  business_name?: string;
  mid?: string;
  business_address?: string;
  business_city?: string;
  business_state?: string;
  business_zip_code?: string;
  business_email?: string;
  business_phone?: string;
  service_type: string;
  service_type_label?: string;
  assigned_to?: string;
  assigned_to_first_name?: string;
  assigned_to_last_name?: string;
  status: string;
  notes?: string;
  created_by?: string;
  created_by_first_name?: string;
  created_by_last_name?: string;
  created_at: string;
  updated_at: string;
  last_comment?: string;
  last_comment_at?: string;
  last_comment_first_name?: string;
  last_comment_last_name?: string;
}

interface ServiceAttachment {
  id: string;
  credit_card_service_id: string;
  comment_id?: string | null;
  filename: string;
  original_filename: string;
  mime_type: string;
  file_size?: number;
  uploaded_at?: string;
}

interface ServiceComment {
  id: string;
  credit_card_service_id: string;
  user_id: string;
  comment: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  created_at: string;
  attachments?: ServiceAttachment[];
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'blue' },
  { value: 'in_progress', label: 'In Progress', color: 'processing' },
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'cancelled', label: 'Cancelled', color: 'default' },
];

const getStatusLabel = (status: string) =>
  STATUS_OPTIONS.find((s) => s.value === status)?.label || status;

const getStatusColor = (status: string) =>
  STATUS_OPTIONS.find((s) => s.value === status)?.color || 'default';

const formatUserName = (first?: string, last?: string) =>
  [first, last].filter(Boolean).join(' ') || '—';

const formatBusinessAddress = (record: Pick<
  CreditCardServiceTask,
  'business_address' | 'business_city' | 'business_state' | 'business_zip_code'
>) => {
  const parts = [
    record.business_address,
    record.business_city,
    record.business_state,
    record.business_zip_code,
  ].filter((x) => x && String(x).trim());
  return parts.length ? parts.join(', ') : null;
};

const truncateCommentPreview = (text?: string, maxLength = 80) => {
  const normalized = (text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return null;
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength)}…`;
};

const slugifyServiceTypeLabel = (label: string) => {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  return slug || `type_${Date.now()}`;
};

const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024;

const filterValidFiles = (files: File[]) => {
  const valid = files.filter((file) => {
    const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
    const isValidSize = file.size <= MAX_ATTACHMENT_SIZE;
    return isValidType && isValidSize;
  });
  if (valid.length !== files.length) {
    message.error('Only images and PDFs under 25MB are allowed');
  }
  return valid;
};

const CreditCardServices: React.FC = () => {
  const { user } = useAuth();
  const canManageCreditCardServices = usePermission('manage_credit_card_services');
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const [taskForm] = Form.useForm();
  const [typeForm] = Form.useForm();
  const taskFileInputRef = useRef<HTMLInputElement>(null);
  const commentFileInputRef = useRef<HTMLInputElement>(null);

  const [taskSelectedFiles, setTaskSelectedFiles] = useState<File[]>([]);
  const [commentSelectedFiles, setCommentSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<ServiceAttachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'types') return 'types';
    if (tabParam === 'tasks') return 'tasks';
    return 'dashboard';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [commentsDrawerVisible, setCommentsDrawerVisible] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editingType, setEditingType] = useState<ServiceType | null>(null);
  const [selectedTask, setSelectedTask] = useState<CreditCardServiceTask | null>(null);
  const [newComment, setNewComment] = useState('');
  const [currentBusiness, setCurrentBusiness] = useState<Business | null>(null);
  const [businessSearchTerm, setBusinessSearchTerm] = useState('');

  const [filterServiceType, setFilterServiceType] = useState<string | undefined>();
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [filterAssignedTo, setFilterAssignedTo] = useState<string | undefined>();
  const [filterCreatedBy, setFilterCreatedBy] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [dashboardTimeframe, setDashboardTimeframe] = useState<DashboardTimeframe>('this_month');
  const [dashboardDateRange, setDashboardDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailTaskForSend, setEmailTaskForSend] = useState<CreditCardServiceTask | null>(null);
  const [emailPreview, setEmailPreview] = useState<CompletionEmailPreview | null>(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [dashboardTasksModal, setDashboardTasksModal] = useState<{
    visible: boolean;
    serviceType: string;
    serviceTypeLabel: string;
    status: 'in_progress' | 'completed';
    statusLabel: string;
  } | null>(null);
  const [tasksPagination, setTasksPagination] = useState({ current: 1, pageSize: 20 });

  const dashboardDateBounds = useMemo(
    () => getDashboardDateRange(dashboardTimeframe, dashboardDateRange),
    [dashboardTimeframe, dashboardDateRange]
  );

  const { data: dashboardData, isLoading: dashboardLoading } = useQuery(
    ['credit-card-services-dashboard-stats', dashboardDateBounds.start.toISOString(), dashboardDateBounds.end.toISOString()],
    async () => {
      const response = await api.get('/api/credit-card-services/dashboard-stats', {
        params: {
          start_date: dashboardDateBounds.start.toISOString(),
          end_date: dashboardDateBounds.end.toISOString(),
        },
      });
      return response.data;
    },
    { enabled: canManageCreditCardServices }
  );
  const dashboardStats: DashboardStats | undefined = dashboardData?.stats;
  const dashboardByType: ServiceTypeDashboardStats[] = dashboardStats?.by_service_type || [];

  const { data: typesData, isLoading: typesLoading } = useQuery(
    'credit-card-service-types',
    async () => {
      const response = await api.get('/api/credit-card-services/types');
      return response.data;
    }
  );
  const serviceTypes: ServiceType[] = typesData?.types || [];

  const { data: agentsData } = useQuery('users-agents', async () => {
    const response = await api.get('/api/users/agents');
    return response.data?.users || [];
  });
  const agentsList: Agent[] = agentsData || [];

  const { data: taskCreatorsData } = useQuery(
    'credit-card-service-task-creators',
    async () => {
      const response = await api.get('/api/credit-card-services/task-creators');
      return response.data?.creators || [];
    },
    { enabled: canManageCreditCardServices }
  );
  const taskCreatorsList: Agent[] = taskCreatorsData || [];

  const { data: businessesData, isLoading: businessesLoading } = useQuery(
    ['businesses-search', businessSearchTerm],
    async () => {
      if (!businessSearchTerm || businessSearchTerm.length < 2) {
        return { businesses: [] };
      }
      const response = await api.get('/api/businesses/search', {
        params: { search: businessSearchTerm, limit: 50 },
      });
      return response.data;
    },
    { enabled: businessSearchTerm.length >= 2 }
  );

  const businesses = businessesData?.businesses || [];
  const allBusinesses =
    currentBusiness && !businesses.find((b: Business) => b.id === currentBusiness.id)
      ? [currentBusiness, ...businesses]
      : businesses;

  const { data: servicesData, isLoading: servicesLoading } = useQuery(
    ['credit-card-services', filterServiceType, filterStatus, filterAssignedTo, filterCreatedBy, dateRange],
    async () => {
      const params: Record<string, string> = {};
      if (filterServiceType) params.service_type = filterServiceType;
      if (filterStatus) params.status = filterStatus;
      if (filterAssignedTo) params.assigned_to = filterAssignedTo;
      if (filterCreatedBy) params.created_by = filterCreatedBy;
      if (dateRange?.[0] && dateRange?.[1]) {
        params.start_date = dateRange[0].startOf('day').toISOString();
        params.end_date = dateRange[1].endOf('day').toISOString();
      }
      const response = await api.get('/api/credit-card-services', { params });
      return response.data;
    },
    { enabled: canManageCreditCardServices }
  );
  const services: CreditCardServiceTask[] = servicesData?.services || [];

  const { data: dashboardTasksData, isLoading: dashboardTasksLoading } = useQuery(
    [
      'credit-card-services-dashboard-tasks',
      dashboardTasksModal?.serviceType,
      dashboardTasksModal?.status,
      dashboardDateBounds.start.toISOString(),
      dashboardDateBounds.end.toISOString(),
    ],
    async () => {
      const response = await api.get('/api/credit-card-services', {
        params: {
          service_type: dashboardTasksModal!.serviceType,
          status: dashboardTasksModal!.status,
          start_date: dashboardDateBounds.start.toISOString(),
          end_date: dashboardDateBounds.end.toISOString(),
        },
      });
      return response.data;
    },
    { enabled: !!dashboardTasksModal?.visible }
  );
  const dashboardTasks: CreditCardServiceTask[] = dashboardTasksData?.services || [];

  const { data: commentsData, isLoading: commentsLoading, refetch: refetchComments } = useQuery(
    ['credit-card-service-comments', selectedTask?.id],
    async () => {
      const response = await api.get(`/api/credit-card-services/${selectedTask!.id}/comments`);
      return response.data;
    },
    { enabled: !!selectedTask?.id && commentsDrawerVisible }
  );
  const comments: ServiceComment[] = commentsData?.comments || [];
  const taskAttachments: ServiceAttachment[] = commentsData?.task_attachments || [];

  const { data: activityData, isLoading: activityLoading, refetch: refetchActivity } = useQuery(
    ['credit-card-service-activity', selectedTask?.id],
    async () => {
      const response = await api.get(`/api/credit-card-services/${selectedTask!.id}/activity`);
      return response.data;
    },
    { enabled: !!selectedTask?.id && commentsDrawerVisible }
  );
  const activityEvents: ActivityEvent[] = activityData?.events || [];

  const uploadAttachments = async (serviceId: string, files: File[]): Promise<string[]> => {
    if (files.length === 0) return [];
    setUploadingFiles(true);
    const uploadedIds: string[] = [];
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('attachment', file);
        const response = await api.post(`/api/credit-card-services/${serviceId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadedIds.push(response.data.attachment.id);
      }
      return uploadedIds;
    } finally {
      setUploadingFiles(false);
    }
  };

  const openAttachmentPreview = async (attachment: ServiceAttachment) => {
    setPreviewAttachment(attachment);
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/credit-card-services/attachments/${attachment.filename}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('Failed to load attachment');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch {
      message.error('Failed to preview attachment');
      setPreviewAttachment(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeAttachmentPreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewAttachment(null);
    setPreviewLoading(false);
  };

  useEffect(() => () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  const renderAttachmentTags = (attachments: ServiceAttachment[], hasCommentText = true) => {
    if (!attachments.length) return null;
    return (
      <div style={{ marginTop: hasCommentText ? 12 : 0, paddingTop: hasCommentText ? 12 : 0, borderTop: hasCommentText ? '1px solid #f0f0f0' : undefined }}>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block', fontWeight: 500 }}>
          Attachments:
        </Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {attachments.map((attachment) => (
            <Tag
              key={attachment.id}
              onClick={() => openAttachmentPreview(attachment)}
              icon={attachment.mime_type?.startsWith('image/') ? <FileImageOutlined /> : <FilePdfOutlined />}
              style={{ cursor: 'pointer', margin: 0 }}
            >
              {attachment.original_filename}
            </Tag>
          ))}
        </div>
      </div>
    );
  };

  const renderSelectedFileTags = (files: File[], onRemove: (index: number) => void) => {
    if (!files.length) return null;
    return (
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
          Attachments ({files.length})
        </Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {files.map((file, index) => (
            <Tag
              key={`${file.name}-${index}`}
              closable
              onClose={() => onRemove(index)}
              icon={file.type.startsWith('image/') ? <FileImageOutlined /> : <FilePdfOutlined />}
            >
              {file.name}
            </Tag>
          ))}
        </div>
      </div>
    );
  };

  const createTaskMutation = useMutation(
    async ({ payload, files }: { payload: Record<string, unknown>; files: File[] }) => {
      const response = await api.post('/api/credit-card-services', payload);
      const service = response.data?.service;
      if (service?.id && files.length > 0) {
        await uploadAttachments(service.id, files);
      }
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['credit-card-services']);
        queryClient.invalidateQueries('credit-card-services-dashboard-stats');
        setTaskModalVisible(false);
        taskForm.resetFields();
        setEditingTaskId(null);
        setCurrentBusiness(null);
        setTaskSelectedFiles([]);
        message.success('Task created successfully');
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.error || 'Failed to create task');
      },
    }
  );

  const updateTaskMutation = useMutation(
    async ({ id, data, files }: { id: string; data: Record<string, unknown>; files: File[] }) => {
      const response = await api.put(`/api/credit-card-services/${id}`, data);
      if (files.length > 0) {
        await uploadAttachments(id, files);
      }
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['credit-card-services']);
        queryClient.invalidateQueries('credit-card-services-dashboard-stats');
        setTaskModalVisible(false);
        taskForm.resetFields();
        setEditingTaskId(null);
        setCurrentBusiness(null);
        setTaskSelectedFiles([]);
        message.success('Task updated successfully');
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.error || 'Failed to update task');
      },
    }
  );

  const deleteTaskMutation = useMutation(
    async (id: string) => {
      await api.delete(`/api/credit-card-services/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['credit-card-services']);
        queryClient.invalidateQueries('credit-card-services-dashboard-stats');
        message.success('Task deleted successfully');
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.error || 'Failed to delete task');
      },
    }
  );

  const createTypeMutation = useMutation(
    async (data: { value: string; label: string }) => {
      const response = await api.post('/api/credit-card-services/types', data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('credit-card-service-types');
        setTypeModalVisible(false);
        typeForm.resetFields();
        setEditingType(null);
        message.success('Service type created successfully');
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.error || 'Failed to create service type');
      },
    }
  );

  const updateTypeMutation = useMutation(
    async ({ value, label }: { value: string; label: string }) => {
      const response = await api.put(`/api/credit-card-services/types/${encodeURIComponent(value)}`, { label });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('credit-card-service-types');
        setTypeModalVisible(false);
        typeForm.resetFields();
        setEditingType(null);
        message.success('Service type updated successfully');
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.error || 'Failed to update service type');
      },
    }
  );

  const deleteTypeMutation = useMutation(
    async (value: string) => {
      await api.delete(`/api/credit-card-services/types/${encodeURIComponent(value)}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('credit-card-service-types');
        message.success('Service type deleted successfully');
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.error || 'Failed to delete service type');
      },
    }
  );

  const addCommentMutation = useMutation(
    async ({ comment, attachmentIds }: { comment: string; attachmentIds: string[] }) => {
      const response = await api.post(`/api/credit-card-services/${selectedTask!.id}/comments`, {
        comment,
        attachment_ids: attachmentIds,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setNewComment('');
        setCommentSelectedFiles([]);
        refetchComments();
        refetchActivity();
        queryClient.invalidateQueries(['credit-card-services']);
        message.success('Comment added');
      },
      onError: () => {
        message.error('Failed to add comment');
      },
    }
  );

  const deleteCommentMutation = useMutation(
    async (commentId: string) => {
      await api.delete(`/api/credit-card-services/comments/${commentId}`);
    },
    {
      onSuccess: () => {
        refetchComments();
        refetchActivity();
        message.success('Comment deleted');
      },
      onError: () => {
        message.error('Failed to delete comment');
      },
    }
  );

  const sendCompletionEmailMutation = useMutation(
    async (taskId: string) => {
      const response = await api.post(`/api/credit-card-services/${taskId}/send-completion-email`);
      return response.data;
    },
    {
      onSuccess: (data, taskId) => {
        message.success(`Email sent to ${data.sent_to}`);
        queryClient.invalidateQueries(['credit-card-service-activity', taskId]);
        refetchActivity();
        setEmailModalVisible(false);
        setEmailTaskForSend(null);
        setEmailPreview(null);
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.error || 'Failed to send email');
      },
    }
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'types') {
      setActiveTab('types');
    } else if (tabParam === 'tasks') {
      setActiveTab('tasks');
    } else {
      setActiveTab('dashboard');
    }
  }, [location.search]);

  const handleTabChange = (key: string) => {
    if (key === 'types') {
      navigate('/credit-card-services?tab=types');
    } else if (key === 'tasks') {
      navigate('/credit-card-services?tab=tasks');
    } else {
      navigate('/credit-card-services?tab=dashboard');
    }
  };

  const handleTaskModalClose = () => {
    setTaskModalVisible(false);
    setEditingTaskId(null);
    setCurrentBusiness(null);
    setTaskSelectedFiles([]);
    taskForm.resetFields();
  };

  const handleTypeModalClose = () => {
    setTypeModalVisible(false);
    setEditingType(null);
    typeForm.resetFields();
  };

  const handleCreateTask = () => {
    setEditingTaskId(null);
    setCurrentBusiness(null);
    setTaskSelectedFiles([]);
    taskForm.resetFields();
    taskForm.setFieldsValue({ status: 'new' });
    setTaskModalVisible(true);
  };

  const handleEditTask = (record: CreditCardServiceTask) => {
    setEditingTaskId(record.id);
    setTaskSelectedFiles([]);
    setCurrentBusiness({
      id: record.business_id,
      business_name: record.business_name || '',
      mid: record.mid,
      address: record.business_address,
      city: record.business_city,
      state: record.business_state,
      zip_code: record.business_zip_code,
    });
    taskForm.setFieldsValue({
      business_id: record.business_id,
      service_type: record.service_type,
      assigned_to: record.assigned_to,
      status: record.status,
      notes: record.notes,
    });
    setTaskModalVisible(true);
  };

  const handleTaskSubmit = async (values: Record<string, unknown>) => {
    const payload = {
      business_id: values.business_id,
      service_type: values.service_type,
      assigned_to: values.assigned_to || null,
      status: values.status || 'new',
      notes: values.notes || null,
    };
    try {
      if (editingTaskId) {
        await updateTaskMutation.mutateAsync({ id: editingTaskId, data: payload, files: taskSelectedFiles });
      } else {
        await createTaskMutation.mutateAsync({ payload, files: taskSelectedFiles });
      }
    } catch {
      // Errors handled in mutation callbacks
    }
  };

  const handleSendComment = async () => {
    if (!selectedTask) return;
    const commentText = newComment.trim();
    if (!commentText && commentSelectedFiles.length === 0) return;
    try {
      let attachmentIds: string[] = [];
      if (commentSelectedFiles.length > 0) {
        attachmentIds = await uploadAttachments(selectedTask.id, commentSelectedFiles);
      }
      await addCommentMutation.mutateAsync({ comment: commentText, attachmentIds });
    } catch {
      message.error('Failed to send comment');
    }
  };

  const handleTaskFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = filterValidFiles(Array.from(event.target.files || []));
    if (files.length) {
      setTaskSelectedFiles((prev) => [...prev, ...files]);
    }
    event.target.value = '';
  };

  const handleCommentFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = filterValidFiles(Array.from(event.target.files || []));
    if (files.length) {
      setCommentSelectedFiles((prev) => [...prev, ...files]);
    }
    event.target.value = '';
  };

  const handleOpenComments = (record: CreditCardServiceTask) => {
    setSelectedTask(record);
    setNewComment('');
    setCommentSelectedFiles([]);
    setCommentsDrawerVisible(true);
  };

  const handleOpenDashboardTasksModal = (
    item: ServiceTypeDashboardStats,
    status: 'in_progress' | 'completed',
    statusLabel: string
  ) => {
    const count = status === 'in_progress' ? item.in_progress : item.completed;
    if (count <= 0) return;
    setDashboardTasksModal({
      visible: true,
      serviceType: item.service_type,
      serviceTypeLabel: item.service_type_label || item.service_type,
      status,
      statusLabel,
    });
  };

  const handleOpenTaskFromDashboardModal = (record: CreditCardServiceTask) => {
    setDashboardTasksModal(null);
    handleOpenComments(record);
  };

  const handleOpenSendEmail = async (record: CreditCardServiceTask, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEmailPreviewLoading(true);
    setEmailTaskForSend(record);
    setEmailModalVisible(true);
    try {
      const response = await api.get(`/api/credit-card-services/${record.id}/completion-email-preview`);
      setEmailPreview(response.data.preview);
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to load email preview');
      setEmailModalVisible(false);
      setEmailTaskForSend(null);
      setEmailPreview(null);
    } finally {
      setEmailPreviewLoading(false);
    }
  };

  const handleCloseEmailModal = () => {
    setEmailModalVisible(false);
    setEmailTaskForSend(null);
    setEmailPreview(null);
    setEmailPreviewLoading(false);
  };

  const renderTaskActions = (record: CreditCardServiceTask) => {
    const isCompleted = record.status === 'completed';

    if (isAdmin) {
      const menuItems = [
        {
          key: 'comment',
          label: 'Add Comment',
          icon: <CommentOutlined />,
          onClick: ({ domEvent }: { domEvent: React.MouseEvent | React.KeyboardEvent }) => {
            domEvent.stopPropagation();
            handleOpenComments(record);
          },
        },
        ...(isCompleted
          ? [{
              key: 'email',
              label: 'Send Email',
              icon: <MailOutlined />,
              onClick: ({ domEvent }: { domEvent: React.MouseEvent | React.KeyboardEvent }) => {
                domEvent.stopPropagation();
                handleOpenSendEmail(record);
              },
            }]
          : []),
        {
          key: 'edit',
          label: 'Edit',
          icon: <EditOutlined />,
          onClick: ({ domEvent }: { domEvent: React.MouseEvent | React.KeyboardEvent }) => {
            domEvent.stopPropagation();
            handleEditTask(record);
          },
        },
        {
          key: 'delete',
          label: 'Delete',
          icon: <DeleteOutlined />,
          danger: true,
          onClick: ({ domEvent }: { domEvent: React.MouseEvent | React.KeyboardEvent }) => {
            domEvent.stopPropagation();
            Modal.confirm({
              title: 'Are you sure you want to delete this task?',
              okText: 'Yes',
              cancelText: 'No',
              okButtonProps: { danger: true },
              onOk: () => deleteTaskMutation.mutateAsync(record.id),
            });
          },
        },
      ];

      return (
        <Dropdown trigger={['click']} menu={{ items: menuItems }}>
          <Button
            type="text"
            icon={<MoreOutlined />}
            aria-label="Task actions"
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      );
    }

    return (
      <Space direction="vertical" size={4} onClick={(e) => e.stopPropagation()}>
        <Button size="small" onClick={() => handleOpenComments(record)}>
          Add Comment
        </Button>
        {isCompleted && (
          <Button size="small" icon={<MailOutlined />} onClick={() => handleOpenSendEmail(record)}>
            Send Email
          </Button>
        )}
      </Space>
    );
  };

  const handleCloseComments = () => {
    setCommentsDrawerVisible(false);
    setSelectedTask(null);
    setNewComment('');
    setCommentSelectedFiles([]);
  };

  const workspaceSectionPadding = 16;

  const renderActivityEventContent = (evt: ActivityEvent) => (
    <div>
      <Text style={{ fontSize: 13, fontWeight: 500, display: 'block' }}>
        {evt.label}
      </Text>
      {evt.detail?.actor && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          by {evt.detail.actor}
        </Text>
      )}
      {evt.detail?.change && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          {evt.detail.change}
        </Text>
      )}
      <Text type="secondary" style={{ fontSize: 12 }}>
        {evt.occurred_at ? dayjs(evt.occurred_at).format('MMM D, YYYY h:mm A') : '—'}
      </Text>
    </div>
  );

  const buildActivityTimelineItems = (events: ActivityEvent[], limit?: number) => {
    const sorted = [...events].reverse();
    const sliced = limit != null ? sorted.slice(0, limit) : sorted;
    return sliced.map((evt, idx) => ({
      key: `${evt.type}-${evt.occurred_at}-${idx}`,
      children: renderActivityEventContent(evt),
    }));
  };

  const renderCommentComposer = () => (
    <div style={{ padding: workspaceSectionPadding, borderBottom: '1px solid #E7E0EC', backgroundColor: '#FEFBFF', flexShrink: 0 }}>
      <Text strong style={{ display: 'block', marginBottom: 10, fontSize: 13 }}>
        Add a comment
      </Text>
      <input
        ref={commentFileInputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        style={{ display: 'none' }}
        onChange={handleCommentFileSelect}
      />
      <div
        style={{
          border: '1px solid #E7E0EC',
          borderRadius: 8,
          backgroundColor: '#fff',
          overflow: 'hidden',
        }}
      >
        {renderSelectedFileTags(commentSelectedFiles, (index) =>
          setCommentSelectedFiles((prev) => prev.filter((_, i) => i !== index))
        )}
        <TextArea
          rows={4}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Type your comment..."
          bordered={false}
          style={{
            resize: 'none',
            minHeight: 120,
            padding: '12px 14px',
            fontSize: 14,
            lineHeight: 1.6,
            background: 'transparent',
          }}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              handleSendComment();
            }
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid #E7E0EC',
            background: '#fafafa',
          }}
        >
          <Button icon={<PaperClipOutlined />} onClick={() => commentFileInputRef.current?.click()}>
            Attach
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSendComment}
            loading={addCommentMutation.isLoading || uploadingFiles}
            disabled={!newComment.trim() && commentSelectedFiles.length === 0}
            style={{ borderRadius: 8 }}
          >
            {uploadingFiles ? 'Uploading...' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderCommentThread = () => (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: workspaceSectionPadding }}>
      {commentsLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : comments.length === 0 && taskAttachments.length === 0 ? (
        <Text type="secondary">No comments yet.</Text>
      ) : (
        <List
          dataSource={[...comments].reverse()}
          style={{ width: '100%' }}
          locale={{ emptyText: '' }}
          renderItem={(item) => {
            const authorFirst = item.first_name || 'U';
            const authorLast = item.last_name || '';
            return (
              <List.Item style={{ marginBottom: 12, alignItems: 'flex-start', padding: 0, width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 10 }}>
                  <Avatar style={{ backgroundColor: '#1890ff', flexShrink: 0, width: 40, height: 40 }}>
                    {authorFirst[0] || 'U'}
                  </Avatar>
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      border: '1px solid #d9d9d9',
                      backgroundColor: '#ffffff',
                      borderRadius: 8,
                      padding: '10px 14px',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <Text style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        {authorFirst} {authorLast}
                      </Text>
                      <Space size="small">
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {dayjs(item.created_at).format('MMM D, YYYY h:mm A')}
                        </Text>
                        {isAdmin && (
                          <Popconfirm
                            title="Delete this comment?"
                            onConfirm={() => deleteCommentMutation.mutate(item.id)}
                          >
                            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                          </Popconfirm>
                        )}
                      </Space>
                    </div>
                    {item.comment ? (
                      <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: 1.6, color: '#262626' }}>
                        {item.comment}
                      </div>
                    ) : (
                      <Text type="secondary" style={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                        No content
                      </Text>
                    )}
                    {renderAttachmentTags(item.attachments || [], Boolean(item.comment))}
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );

  const renderTaskDetailsPanel = () => {
    if (!selectedTask) return null;
    return (
      <>
        <div style={{ padding: workspaceSectionPadding, borderBottom: '1px solid #E7E0EC' }}>
          <Title level={5} style={{ fontWeight: 600, margin: '0 0 12px 0' }}>
            Task Details
          </Title>
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="Service Type">
              {selectedTask.service_type_label || selectedTask.service_type}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedTask.status)}>{getStatusLabel(selectedTask.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Assigned To">
              {formatUserName(selectedTask.assigned_to_first_name, selectedTask.assigned_to_last_name)}
            </Descriptions.Item>
            <Descriptions.Item label="Created By">
              {formatUserName(selectedTask.created_by_first_name, selectedTask.created_by_last_name)}
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {dayjs(selectedTask.created_at).format('MMM D, YYYY h:mm A')}
            </Descriptions.Item>
            <Descriptions.Item label="Last Updated">
              {dayjs(selectedTask.updated_at).format('MMM D, YYYY h:mm A')}
            </Descriptions.Item>
            {selectedTask.notes && (
              <Descriptions.Item label="Notes">
                <div style={{ whiteSpace: 'pre-wrap' }}>{selectedTask.notes}</div>
              </Descriptions.Item>
            )}
          </Descriptions>
          {selectedTask.status === 'completed' && (
            <Button
              size="small"
              icon={<MailOutlined />}
              onClick={() => handleOpenSendEmail(selectedTask)}
              style={{ marginTop: 12 }}
            >
              Send Email
            </Button>
          )}
        </div>

        <div style={{ padding: workspaceSectionPadding, borderBottom: '1px solid #E7E0EC' }}>
          <Title level={5} style={{ fontWeight: 600, margin: '0 0 12px 0' }}>
            Business Information
          </Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <Avatar style={{ backgroundColor: '#1890ff' }}>
              {selectedTask.business_name?.[0] || 'B'}
            </Avatar>
            <div>
              <Text strong style={{ display: 'block' }}>{selectedTask.business_name || '—'}</Text>
              {formatBusinessAddress(selectedTask) && (
                <Text type="secondary" style={{ fontSize: 12 }}>{formatBusinessAddress(selectedTask)}</Text>
              )}
            </div>
          </div>
          <Descriptions size="small" column={1}>
            {selectedTask.mid && <Descriptions.Item label="MID">{selectedTask.mid}</Descriptions.Item>}
            {selectedTask.business_email && (
              <Descriptions.Item label="Email">{selectedTask.business_email}</Descriptions.Item>
            )}
            {selectedTask.business_phone && (
              <Descriptions.Item label="Phone">{selectedTask.business_phone}</Descriptions.Item>
            )}
          </Descriptions>
        </div>

        {taskAttachments.length > 0 && (
          <div style={{ padding: workspaceSectionPadding, borderBottom: '1px solid #E7E0EC' }}>
            <Title level={5} style={{ fontWeight: 600, margin: '0 0 12px 0' }}>
              Task Attachments
            </Title>
            {renderAttachmentTags(taskAttachments, false)}
          </div>
        )}

        <div style={{ padding: workspaceSectionPadding }}>
          <Title level={5} style={{ fontWeight: 600, margin: '0 0 12px 0' }}>
            Activity
          </Title>
          {activityLoading ? (
            <Spin size="small" />
          ) : activityEvents.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>No activity recorded yet.</Text>
          ) : (
            <Timeline items={buildActivityTimelineItems(activityEvents)} />
          )}
        </div>
      </>
    );
  };

  const handleAddType = () => {
    setEditingType(null);
    typeForm.resetFields();
    setTypeModalVisible(true);
  };

  const handleEditType = (record: ServiceType) => {
    setEditingType(record);
    typeForm.setFieldsValue({ label: record.label });
    setTypeModalVisible(true);
  };

  const handleTypeSubmit = (values: { label: string }) => {
    const label = values.label.trim();
    if (editingType) {
      updateTypeMutation.mutate({ value: editingType.value, label });
    } else {
      createTypeMutation.mutate({ value: slugifyServiceTypeLabel(label), label });
    }
  };

  const handleExport = (format: 'csv' | 'excel') => {
    const exportData = services.map((s) => ({
      'Service Type': s.service_type_label || s.service_type,
      Business: s.business_name || '',
      MID: s.mid || '',
      'Assigned To': formatUserName(s.assigned_to_first_name, s.assigned_to_last_name),
      Status: getStatusLabel(s.status),
      Notes: s.notes || '',
      'Created By': formatUserName(s.created_by_first_name, s.created_by_last_name),
      Created: dayjs(s.created_at).format('YYYY-MM-DD HH:mm'),
    }));

    const dateRangeStr =
      dateRange?.[0] && dateRange?.[1]
        ? `_${dateRange[0].format('YYYY-MM-DD')}_to_${dateRange[1].format('YYYY-MM-DD')}`
        : '';

    if (format === 'csv') {
      const headers = Object.keys(exportData[0] || {});
      const csv = [
        headers.join(','),
        ...exportData.map((row) =>
          headers.map((h) => `"${String((row as Record<string, string>)[h] || '').replace(/"/g, '""')}"`).join(',')
        ),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `credit_card_services${dateRangeStr}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, 'Credit Card Services');
      XLSX.writeFile(wb, `credit_card_services${dateRangeStr}.xlsx`);
    }
  };

  const dashboardTaskColumns: ColumnsType<CreditCardServiceTask> = [
    {
      title: 'Business',
      key: 'business',
      render: (_: unknown, record) => {
        const address = formatBusinessAddress(record);
        return (
          <div>
            <div>{record.business_name || '—'}</div>
            {address && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {address}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Assigned To',
      key: 'assigned_to',
      render: (_: unknown, record) =>
        formatUserName(record.assigned_to_first_name, record.assigned_to_last_name),
    },
    {
      title: 'Created By',
      key: 'created_by',
      render: (_: unknown, record) =>
        formatUserName(record.created_by_first_name, record.created_by_last_name),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
  ];

  const taskColumns: ColumnsType<CreditCardServiceTask> = [
    {
      title: 'Service Type',
      dataIndex: 'service_type_label',
      key: 'service_type_label',
      render: (text: string, record) => <Tag color="blue">{text || record.service_type}</Tag>,
    },
    {
      title: 'Business',
      key: 'business',
      render: (_: unknown, record) => {
        const address = formatBusinessAddress(record);
        return (
          <div>
            <div>{record.business_name || '—'}</div>
            {address && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {address}
              </Text>
            )}
            {record.mid && <Text type="secondary" style={{ fontSize: 12 }}>MID: {record.mid}</Text>}
          </div>
        );
      },
    },
    {
      title: 'Assigned To',
      key: 'assigned_to',
      render: (_: unknown, record) =>
        formatUserName(record.assigned_to_first_name, record.assigned_to_last_name),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>,
    },
    {
      title: 'Created By',
      key: 'created_by',
      render: (_: unknown, record) =>
        formatUserName(record.created_by_first_name, record.created_by_last_name),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Last Update',
      key: 'last_update',
      render: (_: unknown, record) => {
        const preview = truncateCommentPreview(record.last_comment);
        if (!preview) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <div style={{ maxWidth: 260 }}>
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.5,
                color: '#262626',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {preview}
            </div>
            {record.last_comment_at && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(record.last_comment_at).format('MMM D, YYYY h:mm A')}
                {record.last_comment_first_name || record.last_comment_last_name
                  ? ` · ${formatUserName(record.last_comment_first_name, record.last_comment_last_name)}`
                  : ''}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: isAdmin ? 80 : 120,
      render: (_: unknown, record) => renderTaskActions(record),
    },
  ];

  const typeColumns: ColumnsType<ServiceType> = [
    { title: 'Service Type', dataIndex: 'label', key: 'label' },
    ...(isAdmin
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, record: ServiceType) => (
              <Space>
                <Tooltip title="Edit">
                  <Button type="text" icon={<EditOutlined />} onClick={() => handleEditType(record)} />
                </Tooltip>
                <Popconfirm
                  title="Are you sure you want to delete this service type?"
                  onConfirm={() => deleteTypeMutation.mutate(record.value)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Tooltip title="Delete">
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          } as ColumnsType<ServiceType>[number],
        ]
      : []),
  ];

  if (!canManageCreditCardServices) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={3}>Access Denied</Title>
        <Text>You do not have permission to view Credit Card Services.</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
      <Title level={2}>Credit Card Services</Title>

      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane tab="Dashboard" key="dashboard">
          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[16, 16]}>
              <Col>
                <Title level={4} style={{ margin: 0 }}>Dashboard</Title>
                <Text type="secondary">
                  {formatDashboardPeriod(
                    dashboardStats?.start_date || dashboardDateBounds.start.toISOString(),
                    dashboardStats?.end_date || dashboardDateBounds.end.toISOString()
                  )}
                </Text>
              </Col>
              <Col>
                <Space wrap>
                  <Select
                    value={dashboardTimeframe}
                    onChange={(value: DashboardTimeframe) => setDashboardTimeframe(value)}
                    style={{ width: 160 }}
                  >
                    {DASHBOARD_TIMEFRAME_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>{option.label}</Option>
                    ))}
                  </Select>
                  {dashboardTimeframe === 'custom' && (
                    <RangePicker
                      value={dashboardDateRange}
                      onChange={(dates) =>
                        setDashboardDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)
                      }
                    />
                  )}
                </Space>
              </Col>
            </Row>
            {dashboardLoading ? (
              <div style={{ textAlign: 'center', padding: 48 }}>
                <Spin size="large" />
              </div>
            ) : dashboardByType.length === 0 ? (
              <Text type="secondary">No service types configured.</Text>
            ) : (
              <Row gutter={[16, 16]}>
                {dashboardByType.map((item) => {
                  const isEmpty = item.in_progress === 0 && item.completed === 0;
                  return (
                    <Col xs={24} sm={12} lg={8} xl={6} key={item.service_type}>
                      <Card
                        size="small"
                        style={{
                          height: '100%',
                          opacity: isEmpty ? 0.75 : 1,
                          borderColor: isEmpty ? '#f0f0f0' : undefined,
                        }}
                      >
                        <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 15 }}>
                          {item.service_type_label || item.service_type}
                        </Text>
                        <Row gutter={12}>
                          <Col span={12}>
                            <div
                              role={item.in_progress > 0 ? 'button' : undefined}
                              tabIndex={item.in_progress > 0 ? 0 : undefined}
                              style={{
                                cursor: item.in_progress > 0 ? 'pointer' : 'default',
                                borderRadius: 8,
                                padding: item.in_progress > 0 ? '4px 0' : undefined,
                              }}
                              onClick={() => handleOpenDashboardTasksModal(item, 'in_progress', 'In Progress')}
                              onKeyDown={(e) => {
                                if (item.in_progress > 0 && (e.key === 'Enter' || e.key === ' ')) {
                                  e.preventDefault();
                                  handleOpenDashboardTasksModal(item, 'in_progress', 'In Progress');
                                }
                              }}
                            >
                              <Statistic
                                title="In Progress"
                                value={item.in_progress}
                                prefix={<SyncOutlined />}
                                valueStyle={{ color: '#1890ff', fontSize: 24 }}
                              />
                            </div>
                          </Col>
                          <Col span={12}>
                            <div
                              role={item.completed > 0 ? 'button' : undefined}
                              tabIndex={item.completed > 0 ? 0 : undefined}
                              style={{
                                cursor: item.completed > 0 ? 'pointer' : 'default',
                                borderRadius: 8,
                                padding: item.completed > 0 ? '4px 0' : undefined,
                              }}
                              onClick={() => handleOpenDashboardTasksModal(item, 'completed', 'Completed')}
                              onKeyDown={(e) => {
                                if (item.completed > 0 && (e.key === 'Enter' || e.key === ' ')) {
                                  e.preventDefault();
                                  handleOpenDashboardTasksModal(item, 'completed', 'Completed');
                                }
                              }}
                            >
                              <Statistic
                                title="Completed"
                                value={item.completed}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ color: '#52c41a', fontSize: 24 }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card>
        </TabPane>

        <TabPane tab="Tasks" key="tasks">
          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[16, 16]}>
              <Col>
                <Title level={4} style={{ margin: 0 }}>Tasks</Title>
              </Col>
              <Col>
                <Space wrap>
                  <Select
                    allowClear
                    placeholder="Service type"
                    style={{ width: 200 }}
                    value={filterServiceType}
                    onChange={setFilterServiceType}
                  >
                    {serviceTypes.map((t) => (
                      <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                  </Select>
                  <Select
                    allowClear
                    placeholder="Status"
                    style={{ width: 140 }}
                    value={filterStatus}
                    onChange={setFilterStatus}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <Option key={s.value} value={s.value}>{s.label}</Option>
                    ))}
                  </Select>
                  <Select
                    allowClear
                    placeholder="Assigned to"
                    style={{ width: 180 }}
                    value={filterAssignedTo}
                    onChange={setFilterAssignedTo}
                    showSearch
                    optionFilterProp="children"
                  >
                    {agentsList.map((a) => (
                      <Option key={a.id} value={a.id}>
                        {formatUserName(a.first_name, a.last_name)}
                      </Option>
                    ))}
                  </Select>
                  <Select
                    allowClear
                    placeholder="Created by"
                    style={{ width: 180 }}
                    value={filterCreatedBy}
                    onChange={setFilterCreatedBy}
                    showSearch
                    optionFilterProp="children"
                  >
                    {taskCreatorsList.map((a) => (
                      <Option key={a.id} value={a.id}>
                        {formatUserName(a.first_name, a.last_name)}
                      </Option>
                    ))}
                  </Select>
                  <RangePicker
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)}
                  />
                  <Dropdown
                    menu={{
                      items: [
                        { key: 'csv', label: 'Export CSV', onClick: () => handleExport('csv') },
                        { key: 'excel', label: 'Export Excel', onClick: () => handleExport('excel') },
                      ],
                    }}
                  >
                    <Button icon={<DownloadOutlined />}>Export</Button>
                  </Dropdown>
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateTask}>
                    New Task
                  </Button>
                </Space>
              </Col>
            </Row>

            <Table
              columns={taskColumns}
              dataSource={services}
              rowKey="id"
              loading={servicesLoading}
              pagination={{
                current: tasksPagination.current,
                pageSize: tasksPagination.pageSize,
                showSizeChanger: true,
                onChange: (page, pageSize) => {
                  setTasksPagination({ current: page, pageSize });
                },
              }}
              onRow={(record) => ({
                onClick: () => handleOpenComments(record),
                style: { cursor: 'pointer' },
              })}
            />
          </Card>
        </TabPane>

        <TabPane tab="Service Types" key="types">
          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Title level={4} style={{ margin: 0 }}>Service Types</Title>
              </Col>
              {isAdmin && (
                <Col>
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddType}>
                    Add Type
                  </Button>
                </Col>
              )}
            </Row>
            <Table
              columns={typeColumns}
              dataSource={serviceTypes}
              rowKey="value"
              loading={typesLoading}
              pagination={false}
            />
          </Card>
        </TabPane>
      </Tabs>

      <Modal
        title={editingTaskId ? 'Edit Task' : 'New Task'}
        open={taskModalVisible}
        onCancel={handleTaskModalClose}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form form={taskForm} layout="vertical" onFinish={handleTaskSubmit}>
          <Form.Item
            name="business_id"
            label="Business"
            rules={[{ required: true, message: 'Please select a business' }]}
          >
            <Select
              showSearch
              placeholder="Search business by name, MID, or address"
              filterOption={false}
              onSearch={setBusinessSearchTerm}
              loading={businessesLoading}
              notFoundContent={businessSearchTerm.length < 2 ? 'Type at least 2 characters' : undefined}
            >
              {allBusinesses.map((b: Business) => (
                <Option key={b.id} value={b.id}>
                  {b.business_name}
                  {b.mid ? ` (MID: ${b.mid})` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="service_type"
            label="Service Type"
            rules={[{ required: true, message: 'Please select a service type' }]}
          >
            <Select placeholder="Select service type">
              {serviceTypes.map((t) => (
                <Option key={t.value} value={t.value}>{t.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="assigned_to" label="Assigned To">
            <Select allowClear placeholder="Select agent" showSearch optionFilterProp="children">
              {agentsList.map((a) => (
                <Option key={a.id} value={a.id}>
                  {formatUserName(a.first_name, a.last_name)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select placeholder="Select status">
              {STATUS_OPTIONS.map((s) => (
                <Option key={s.value} value={s.value}>{s.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Optional notes" />
          </Form.Item>

          <Form.Item label="Attachments">
            <input
              ref={taskFileInputRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              style={{ display: 'none' }}
              onChange={handleTaskFileSelect}
            />
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                icon={<PaperClipOutlined />}
                onClick={() => taskFileInputRef.current?.click()}
              >
                Add attachments
              </Button>
              {renderSelectedFileTags(taskSelectedFiles, (index) =>
                setTaskSelectedFiles((prev) => prev.filter((_, i) => i !== index))
              )}
            </Space>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createTaskMutation.isLoading || updateTaskMutation.isLoading || uploadingFiles}
              >
                {editingTaskId ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleTaskModalClose}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingType ? 'Edit Service Type' : 'Add Service Type'}
        open={typeModalVisible}
        onCancel={handleTypeModalClose}
        footer={null}
        width={400}
        destroyOnClose
      >
        <Form form={typeForm} layout="vertical" onFinish={handleTypeSubmit}>
          <Form.Item
            name="label"
            label="Service Type"
            rules={[{ required: true, message: 'Please enter a service type name' }]}
          >
            <Input placeholder="e.g. Dejavoo Pricing Update" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createTypeMutation.isLoading || updateTypeMutation.isLoading}
              >
                {editingType ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleTypeModalClose}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={
          selectedTask ? (
            <div>
              <div>{selectedTask.service_type_label || selectedTask.service_type}</div>
              <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
                {selectedTask.business_name}
              </Text>
            </div>
          ) : 'Task'
        }
        open={commentsDrawerVisible}
        onClose={handleCloseComments}
        width={920}
        styles={{ body: { display: 'flex', flexDirection: 'column', padding: 0, background: '#FEFBFF', overflow: 'hidden' } }}
      >
        {selectedTask && (
          <div style={{ display: 'flex', flex: 1, minHeight: 0, height: '100%' }}>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid #E7E0EC',
                background: '#fff',
              }}
            >
              {renderCommentComposer()}
              {renderCommentThread()}
            </div>
            <div
              style={{
                width: 340,
                flexShrink: 0,
                overflowY: 'auto',
                background: '#fff',
              }}
            >
              {renderTaskDetailsPanel()}
            </div>
          </div>
        )}
      </Drawer>

      <Modal
        title="Send Email"
        open={emailModalVisible}
        onCancel={handleCloseEmailModal}
        okText="Send Email"
        okButtonProps={{
          icon: <MailOutlined />,
          disabled: !emailPreview?.to || emailPreviewLoading,
          loading: sendCompletionEmailMutation.isLoading,
        }}
        onOk={() => {
          if (emailTaskForSend) {
            sendCompletionEmailMutation.mutate(emailTaskForSend.id);
          }
        }}
        destroyOnClose
      >
        {emailPreviewLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text type="secondary">Business</Text>
              <div><Text strong>{emailPreview?.business_name || emailTaskForSend?.business_name || '—'}</Text></div>
            </div>
            <div>
              <Text type="secondary">Recipient</Text>
              <div><Text>{emailPreview?.to || '—'}</Text></div>
            </div>
            <div>
              <Text type="secondary">Subject</Text>
              <div><Text>{emailPreview?.subject || '—'}</Text></div>
            </div>
            {!emailPreview?.to && (
              <Alert
                type="warning"
                showIcon
                message="This business does not have an email address on file. Add an email to the business record before sending."
              />
            )}
          </Space>
        )}
      </Modal>

      <Modal
        title={
          dashboardTasksModal
            ? `${dashboardTasksModal.serviceTypeLabel} — ${dashboardTasksModal.statusLabel}`
            : 'Tasks'
        }
        open={!!dashboardTasksModal?.visible}
        onCancel={() => setDashboardTasksModal(null)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Table
          columns={dashboardTaskColumns}
          dataSource={dashboardTasks}
          rowKey="id"
          loading={dashboardTasksLoading}
          pagination={false}
          size="small"
          onRow={(record) => ({
            onClick: () => handleOpenTaskFromDashboardModal(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Modal>

      <Modal
        title={previewAttachment?.original_filename || 'Attachment preview'}
        open={!!previewAttachment}
        onCancel={closeAttachmentPreview}
        footer={null}
        width={previewAttachment?.mime_type?.startsWith('image/') ? 720 : 900}
        destroyOnClose
      >
        {previewLoading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : previewUrl && previewAttachment ? (
          previewAttachment.mime_type?.startsWith('image/') ? (
            <img
              src={previewUrl}
              alt={previewAttachment.original_filename}
              style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          ) : (
            <iframe
              src={previewUrl}
              title={previewAttachment.original_filename}
              style={{ width: '100%', height: '70vh', border: 'none' }}
            />
          )
        ) : null}
      </Modal>
    </div>
  );
};

export default CreditCardServices;
